// Scan history normalizers and breakdown.

import { DATE_RANGE_FILTER_OPTIONS, VALID_DATE_RANGE_KEYS } from 'src/services/consts/app.const'
import { SCAN_STATUS } from 'src/services/consts/shared.const'
import {
  SCAN_HISTORY_DEFAULT_FILTER,
  SCAN_HISTORY_DEFAULT_SORT,
  SCAN_HISTORY_QUERY_KEYS,
  SCAN_HISTORY_STATUS_ACTIVE_KEY,
  SCAN_HISTORY_STATUS_ALL_KEY,
  VALID_SCAN_HISTORY_SORT_BY,
  VALID_SCAN_HISTORY_STATUS_KEYS,
} from 'src/services/consts/scans/history.const'
import { isActiveScanStatus, normalizeScanType } from 'src/services/helpers/scan/status.helper'
import {
  getCutoffDate,
  getFilterFromQuery,
  getSortAndPaginationFromQuery,
  mapFromQueryParams,
} from 'src/services/helpers/util.helper'

/**
 * @typedef {Object} ScanHistoryItem
 * @property {string|null} scanId
 * @property {string} status
 * @property {string} scanType - SCAN_TYPE value ('preview' | 'full')
 * @property {number|null} pagesScanned
 * @property {number|null} totalPagesFound
 * @property {string|null} startedAt
 * @property {string|null} completedAt
 * @property {import('../scan/status.helper').ScanSummary|null} summary
 */

/** @returns {ScanHistoryItem|null} */
export function scanStatusToHistoryItem(scan) {
  if (!scan?.status) return null
  return {
    scanId: scan.scanId ?? null,
    status: scan.status,
    scanType: normalizeScanType(scan.scanType),
    pagesScanned: scan.pagesScanned ?? null,
    totalPagesFound: scan.totalPagesFound ?? null,
    startedAt: scan.startedAt || null,
    completedAt: scan.completedAt || null,
    summary: scan.results?.summary ?? null,
  }
}

/** @returns {ScanHistoryItem|null} */
function normalizeScanHistoryItem(raw) {
  if (!raw || typeof raw !== 'object') return null
  const status = raw.status ?? null
  if (!status) return null
  return {
    scanId: raw.scanId ?? null,
    status,
    scanType: normalizeScanType(raw.scanType),
    pagesScanned: raw.pagesScanned ?? null,
    totalPagesFound: raw.totalPagesFound ?? null,
    startedAt: raw.startedAt || null,
    completedAt: raw.completedAt || null,
    summary: raw.summary ?? null,
  }
}

/** @returns {ScanHistoryItem[]} */
export function normalizeScanHistory(raw) {
  return (raw?.scans || []).map(normalizeScanHistoryItem).filter(Boolean)
}

/**
 * Effective timestamp used by the scan-history date column (`completedAt || startedAt`).
 * Missing or invalid dates compare as epoch so they never beat a real later date.
 * @param {ScanHistoryItem} scan
 * @returns {number}
 */
function getScanHistoryEffectiveTime(scan) {
  const time = new Date(scan.completedAt || scan.startedAt || 0).getTime()
  return Number.isFinite(time) ? time : 0
}

/**
 * Most recent completed/failed scan id by the same effective date as the history
 * table, independent of backend array order. Does not mutate `scanHistory`.
 * Equal timestamps keep the first-seen row.
 * @param {ScanHistoryItem[]} scanHistory
 * @returns {string|null}
 */
export function getLastTerminalScanId(scanHistory) {
  const list = Array.isArray(scanHistory) ? scanHistory : []
  let latest = null
  let latestTime = -Infinity

  for (const scan of list) {
    if (scan.status !== SCAN_STATUS.COMPLETED && scan.status !== SCAN_STATUS.FAILED) {
      continue
    }
    const time = getScanHistoryEffectiveTime(scan)
    if (!latest || time > latestTime) {
      latest = scan
      latestTime = time
    }
  }

  return latest?.scanId ?? null
}

/**
 * @typedef {Object} ScanHistoryStatusGroup
 * @property {string} key - SCAN_STATUS value or SCAN_HISTORY_STATUS_ACTIVE_KEY
 * @property {string} label
 * @property {ScanHistoryItem[]} scans
 */

/** @param {ScanHistoryItem[]} scanHistory @returns {ScanHistoryStatusGroup[]} */
export function getScanHistoryStatusBreakdown(scanHistory) {
  const list = Array.isArray(scanHistory) ? scanHistory : []
  const completed = []
  const failed = []
  const inProgress = []

  for (const row of list) {
    if (isActiveScanStatus(row.status)) {
      inProgress.push(row)
    } else if (row.status === SCAN_STATUS.COMPLETED) {
      completed.push(row)
    } else if (row.status === SCAN_STATUS.FAILED) {
      failed.push(row)
    }
  }

  return [
    { key: SCAN_STATUS.COMPLETED, label: 'Completed', scans: completed },
    { key: SCAN_STATUS.FAILED, label: 'Failed', scans: failed },
    { key: SCAN_HISTORY_STATUS_ACTIVE_KEY, label: 'In progress', scans: inProgress },
  ]
}

/**
 * Builds the sidebar filter item list from a status breakdown.
 * @param {number} totalCount - total scan count (for the "All scans" entry)
 * @param {ScanHistoryStatusGroup[]} breakdown - output of getScanHistoryStatusBreakdown
 * @returns {{ key: string, label: string, count: number }[]}
 */
export function getScanHistoryStatusFilterItems(totalCount, breakdown) {
  return [
    { key: SCAN_HISTORY_STATUS_ALL_KEY, label: 'All scans', count: totalCount },
    ...breakdown.map(({ key, label, scans }) => ({ key, label, count: scans.length })),
  ]
}

/**
 * Builds date range filter options with counts from a scan history list.
 * @param {ScanHistoryItem[]} scans - status-filtered scan history rows
 * @returns {{ value: string|null, label: string, icon: string, count: number }[]}
 */
export function getScanHistoryDateRangeFilterOptions(scans) {
  const list = Array.isArray(scans) ? scans : []

  return DATE_RANGE_FILTER_OPTIONS.map(({ value, label, icon }) => {
    if (!value) {
      return { value, label, icon, count: list.length }
    }

    const cutoff = getCutoffDate(value)
    if (!cutoff) {
      return { value, label, icon, count: 0 }
    }

    const cutoffTime = cutoff.getTime()
    let count = 0
    for (const scan of list) {
      if (!scan?.startedAt) continue
      const startedTime = new Date(scan.startedAt).getTime()
      if (!Number.isNaN(startedTime) && startedTime >= cutoffTime) {
        count += 1
      }
    }

    return { value, label, icon, count }
  })
}

/**
 * URL → history scanStatus/dateRange filter. Namespaced keys remap onto `getFilterFromQuery`.
 * Invalid `dateRange` falls back to default; invalid `scanStatus` falls back to "all".
 * @param {Object} query
 */
export function getScanHistoryFilterFromQuery(query) {
  const filterBy = getFilterFromQuery(
    SCAN_HISTORY_DEFAULT_FILTER,
    mapFromQueryParams(query, SCAN_HISTORY_QUERY_KEYS)
  )
  if (filterBy.dateRange && !VALID_DATE_RANGE_KEYS.has(filterBy.dateRange)) {
    filterBy.dateRange = null
  }
  if (filterBy.scanStatus && !VALID_SCAN_HISTORY_STATUS_KEYS.has(filterBy.scanStatus)) {
    filterBy.scanStatus = SCAN_HISTORY_STATUS_ALL_KEY
  }
  return filterBy
}

/**
 * URL → history table state. Namespaced keys remap onto `getSortAndPaginationFromQuery`.
 * @param {Object} query
 * @param {number} [totalItems] - When provided, page is clamped to valid range
 * @returns {{ tableState: { sortBy: string, descending: boolean, page: number, rowsPerPage: number }, needsUrlHeal: boolean }}
 */
export function getScanHistorySortAndPaginationFromQuery(query, totalItems) {
  const { tableState, needsUrlHeal } = getSortAndPaginationFromQuery(
    SCAN_HISTORY_DEFAULT_SORT,
    mapFromQueryParams(query, SCAN_HISTORY_QUERY_KEYS),
    totalItems
  )
  if (!VALID_SCAN_HISTORY_SORT_BY.has(tableState.sortBy)) {
    tableState.sortBy = SCAN_HISTORY_DEFAULT_SORT.sortBy
  }
  return { tableState, needsUrlHeal }
}
